/**
 * User: Joe Forzano
 * Date: August 20th, 2014
 * Time: 3:15 PM
 */
"use strict";

// ---------------------------------------------------
// Load Module Super PseudoClass

var SoaCollection = require('bunsen/collections/SoaCollection');


// ---------------------------------------------------
// Module Definition
/**
 * @class Carriers
 * @extends SoaCollection
 */
var Carriers = module.exports = SoaCollection.extend();


// ---------------------------------------------------
// Dependencies

var _ = require('underscore');
var $ = require('dibs-jquery-shim');
var endpoints = require('dibs-endpoints');
var Carrier = require('bunsen/models/logistics/Carrier');
var Clump = require('bunsen/libraries/clump');
var SV = require('server-vars');
var getDeferredMethods = require('bunsen/helpers/getDeferredMethods');
var globalCache = require('bunsen/helpers/globalCache');

var deferred = getDeferredMethods().Deferred;


// ---------------------------------------------------
// Define Module Instance Properties

_.extend(Carriers.prototype, {
    model: Carrier,

    /**
     * Builds the url to fetch carriers based on the urlOptions hash passed in during
     * collection creation
     * @returns {string} url
     */
    url: endpoints.carriersBase(),

    /**
     * @param {Carrier[]} models
     * @param {Object} options
     * @param {Object} options.urlOptions - options to pass as query params for the collection fetch
     * @param {string} options.urlOptions.view - name of view to filter carriers for
     * @param {number | number[]} options.urlOptions.ids - id/ids to filter carriers for
     * @param {string | string[]} options.urlOptions.codes - code/codes to filter carriers for
     * @param {string | string[]} options.urlOptions.fields - fields to inflate on fetched carriers
     */
    initialize: function (models, options) {
        options = options || {};
        this.urlOptions = options.urlOptions;

        Carriers.__super__.initialize.call(this, models, options);
    },

    /**
     * Builds a hash mapping carrier ids to their display names
     * @returns {Object}
     */
    getIdNameMap: function () {
        if (this.idNameMap && !_.isEmpty(this.idNameMap)) {
            return this.idNameMap;
        }

        this.idNameMap = this.map(function (model) {
            return {
                value: model.get('id'),
                display: model.get('name')
            };
        });

        return this.idNameMap;

    },

    /**
     * Builds an array of objects containing ids and names.
     * @returns {Array}
     */
    getIdNameArray: function () {
        if (this.idNameArray && !_.isEmpty(this.idNameArray)) {
            return this.idNameArray;
        }

        var arr = [];
        this.each(function (model) {
            arr.push({
                id: model.get('id'),
                name: model.get('name')
            });
        });

        return arr;
    },

    /**
     * Looks up the carrier associated with the given id
     * and returns true if that carrier supports tracking links
     * @param {number} carrierId
     * @returns {Boolean}
     */
    canCarrierSupportTrackingLink: function (carrierId) {
        var carrier = this.findWhere({id: parseInt(carrierId, 10)});
        return carrier.get('supportTrackingLink');

    }

});


// ---------------------------------------------------
// Define Module Static Properties

_.extend(Carriers, {

    VIEW_INTERNAL: 'INTERNAL',
    VIEW_SELLER: 'SELLER',

    OTHER_ID: 62,

    _carriersByView: {},
    _fetchingCarriersByView: {},

    /**
     * @static
     * @private
     * @param {string} view
     * @returns {symbol}
     */
    _getCarrierCacheKeyForView: (function () {
        var cacheKeys = {};
        return function (view) {
            if (!cacheKeys[view]) {
                cacheKeys[view] = Symbol('fetching_carriers_' + view);
            }
            return cacheKeys[view];
        };
    }()),

    /**
     * Creates and/or returns the singleton promise for the carriers being fetched
     * for the given view name. If server vars have carriers defined for the view (under
     * carriers.[viewName], we'll circumvent the fetch and return a resolved promise
     * with that data.
     * @param {string} view (i.e. SELLER, INTERNAL)
     * @param {Clump} [clump] clump instance
     * @param {Object} [queryData] optional query params for the carriers fetch
     * @returns {Promise} promise for carriers collection fetch
     */
    getCarriersPromiseForView: function (view, clump, queryData) {
        var models = SV.get('carriers.' + view) || [];
        var cacheKey = Carriers._getCarrierCacheKeyForView(view);
        var fetchCarriersProm = globalCache.get(cacheKey);
        var data = _.extend({
            view: view
        }, queryData);

        if (fetchCarriersProm) {
            return fetchCarriersProm;
        }

        if (!Carriers._carriersByView[view]) {
            Carriers._carriersByView[view] = new Carriers(models, {
                urlOptions: {
                    view: view
                }
            });
        }

        if (models.length) {
            fetchCarriersProm = deferred().resolve(models).promise();
        } else {
            fetchCarriersProm = Carriers._carriersByView[view].fetch({
                clump: clump,
                data: data
            });
        }

        globalCache.set(cacheKey, fetchCarriersProm);
        return fetchCarriersProm;
    },

    /**
     * Creates and/or returns the singleton promise wrapping all promises for the
     * passed list of views names.
     * @param {string[]} viewList (i.e. SELLER, INTERNAL)
     * @param {Clump} [clump] clump instance
     * * @param {Object} [queryData] optional query params for the carriers fetch
     * @returns {Promise} promise for all carriers collection fetches
     */
    getCarriersPromiseForViews: function (viewList, clump, queryData) {
        var ownClump = false;
        var viewKey = viewList.join('');
        var promises = [];
        var currentPromise;

        if (Carriers._fetchingCarriersByView[viewKey]) {
            return Carriers._fetchingCarriersByView[viewKey];
        }

        if (!clump) {
            clump = new Clump();
            ownClump = true;
        }

        _.each(viewList, function(view) {
            currentPromise = Carriers.getCarriersPromiseForView(view, clump, queryData);
            promises.push(currentPromise);
        });

        if (ownClump) {
            clump.run();
        }

        Carriers._fetchingCarriersByView[viewKey] = $.when.apply(promises);
        return Carriers._fetchingCarriersByView[viewKey];
    },

    /**
     * If the collection has not been instantiated for the requested view, a new collection
     * is created before returning the singleton collection. If carrier data is present on server
     * vars for the requested view, the collection is populated with that data.
     * @param view
     * @returns {Carriers} singleton carriers collection for the given view
     */
    getCarriersForView: function (view) {
        var models = SV.get('carriers.' + view) || [];

        if (!Carriers._carriersByView[view]) {
            Carriers._carriersByView[view] = new Carriers(models, {
                urlOptions: {
                    view: view
                }
            });
        }

        return Carriers._carriersByView[view];
    },

    /**
     * @param view
     * @returns {Object} the id-name mapping for the singleton carriers collection for the given view
     */
    getIdNameMapForView: function (view) {
        return Carriers._carriersByView[view].getIdNameMap();
    },

    /**
     * @param view
     * @returns {Object} the id-name mapping for the singleton carriers collection for the given view
     */
    getIdNameArrayForView: function (view) {
        return Carriers._carriersByView[view].getIdNameArray();
    },

    /**
     * Searches all fetched carriers for the model having the given id and returns the name associated with that carrier
     * @param {number|string} carrierId
     * @returns {string}
     */
    getCarrierName: function (carrierId) {
        var carrier = this.findCarrierForId(carrierId);
        return carrier ? carrier.get('name') : null;
    },

    /**
     * Search through the singleton carriers collection for a carrier
     * matching the given id and return their code if found
     * @param {string|number} carrierId
     * @returns {string}
     */
    getCarrierCode: function (carrierId) {
        var carrier = this.findCarrierForId(carrierId);
        return carrier ? carrier.get('code') : null;
    },

    /**
     * Search through the singleton carriers collection for a carrier matching
     * the provided id.
     * @param {string|number} carrierId
     * @returns {Carrier}
     */
    findCarrierForId: function (carrierId) {
        var allCarrierModelLists = _.pluck(_.values(Carriers._carriersByView), 'models');
        var allCarrierModels = _.flatten(allCarrierModelLists);
        return _.find(allCarrierModels, function (carrier) {
            return String(carrier.get('id')) === String(carrierId);
        });
    }


});



// WEBPACK FOOTER //
// ./~/bunsen/collections/logistics/Carriers.es.js